import React, { useEffect, useState } from "react";

import { CheckCircle2, Circle, Clock, Loader2 } from "lucide-react";

import { bqCountText, bqSummary, briefingStalled } from "./briefingAnswers";
import { StepItems, type SubItem } from "./UploadProgress";
import type { PlanFile, PlanProgressStep } from "../../Types/AccountPlan";
import { STALLED_LINE } from "../../Utils/AccountPlans/queueText";

interface Props {
  file: PlanFile;
  /** "done": the finished run, read back on the Plan tab (product review 2026-11-01). */
  variant?: "live" | "done";
}

/** "stalled": the server gave up on a pass after its retries (D29). P8 (Sushant,
 * 2026-11-03): that reads as taking longer than usual, never as a red failure. */
type RowStatus = "done" | "active" | "pending" | "stalled";

interface Row {
  key: string;
  label: string;
  status: RowStatus;
  ms?: number;
  startedAt?: string;
  items?: SubItem[];
}

const rowStatus = (s?: PlanProgressStep): RowStatus =>
  s?.state === "done"
    ? "done"
    : s?.state === "active"
      ? "active"
      : s?.state === "failed"
        ? "stalled"
        : "pending";

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? "" : "s"}`;

/** Items kept after the check against the slides, across every section. */
export const itemsChecked = (file: PlanFile): number =>
  (file.progress?.briefing?.steps?.verify?.items ?? []).reduce(
    (n, i) => n + (i.count ?? 0),
    0
  );

/**
 * "9 sections · 5 Briefing Questions answered · 33 items checked against the
 * slides": what a finished run produced, for the Plan tab's summary.
 */
export const briefingRunSummary = (file: PlanFile): string => {
  const steps = file.progress?.briefing?.steps;
  const sections = steps?.write?.items?.length ?? 0;
  // The same per-question state the Briefing Questions tab reads (UI tour item 5).
  const questions = bqCountText(bqSummary(file));
  const checked = itemsChecked(file);
  return [
    sections && plural(sections, "section"),
    questions,
    checked && `${plural(checked, "item")} checked against the slides`,
  ]
    .filter(Boolean)
    .join(" · ");
};

/**
 * Rows for the Review briefing checklist, from the server's progress.briefing
 * (backend progress.py, BriefingProgress). The briefing and the briefing
 * questions are two AI passes that really run side by side, so both can be in
 * progress at once; every kept item is then checked against its slide.
 */
export const briefingRows = (file: PlanFile): Row[] | null => {
  const steps = file.progress?.briefing?.steps;
  if (!steps) return null;
  const write = steps.write;
  const questions = steps.questions;
  const verify = steps.verify;
  const chat = steps.chat;
  const sections = write?.items?.length ?? 0;
  const asked = questions?.items?.length ?? 0;
  const checked = itemsChecked(file);
  const vN = `v${file.plan_version ?? file.version}`;
  // Answers are saved with the briefing: until it is ready, none show anywhere else.
  const saved = file.sponsor_view_status === "ready";
  // A stalled pass lists only what it finished; the rest isn't a failure to show.
  const items = (s?: PlanProgressStep): SubItem[] | undefined => {
    const kept = s?.items
      ?.filter(i => i.state !== "failed")
      .map(i => ({
        label:
          i.count != null ? `${i.label} · ${plural(i.count, "item")}` : i.label,
        status: i.state as SubItem["status"],
      }));
    return kept?.length ? kept : undefined;
  };
  const rows: Row[] = [
    {
      key: "write",
      label:
        write?.state === "done"
          ? `Wrote the briefing · ${plural(sections, "section")}`
          : sections
            ? `Writing the briefing · ${plural(sections, "section")}`
            : "Writing the briefing",
      status: rowStatus(write),
      ms: write?.ms,
      startedAt: write?.started_at,
      items: items(write),
    },
    {
      key: "questions",
      label:
        questions?.state === "done"
          ? saved
            ? `Answered the Briefing Questions · ${asked}`
            : `Answered the Briefing Questions · ${asked} · shown once the briefing is ready`
          : asked
            ? `Answering the Briefing Questions · ${asked}`
            : "Answering the Briefing Questions",
      status: rowStatus(questions),
      ms: questions?.ms,
      startedAt: questions?.started_at,
      items: items(questions),
    },
    {
      key: "verify",
      label:
        verify?.state === "done" && checked
          ? `Checked ${plural(checked, "item")} against the slides they quote`
          : "Checking every item against the slide it quotes",
      status: rowStatus(verify),
      items: items(verify),
    },
    {
      // Chat answers from this version's slides, briefing and briefing-question
      // answers (with the suggested questions from that pass): ready the moment
      // the briefing is saved, which the server records in the same write.
      key: "chat",
      label:
        chat?.state === "done"
          ? `Prepared Ask about this plan for ${vN} · opens once published`
          : `Preparing Ask about this plan for ${vN}`,
      status: chat
        ? rowStatus(chat)
        : verify?.state === "done"
          ? "active"
          : "pending",
      items: chat
        ? items(chat)
        : verify?.state === "done"
          ? [
              "Answers to the briefing questions",
              "The briefing's sections",
              "The plan's slides",
            ].map(label => ({ label, status: "active" as const }))
          : undefined,
    },
    {
      key: "ready",
      label: "Ready for your review",
      status: file.sponsor_view_status === "ready" ? "done" : "pending",
    },
  ];
  // Stalled by the job limit (jobs._stall_plan: progress.stalled, status back to
  // "processing"), often with every pass done and nothing saved: the first step not
  // done is where it stopped (UI tour item 1).
  if (briefingStalled(file) && !rows.some(r => r.status === "stalled")) {
    const stuck = rows.find(r => r.status !== "done");
    if (stuck) stuck.status = "stalled";
  }
  return rows;
};

const seconds = (ms: number) =>
  ms < 10_000 ? `${(ms / 1000).toFixed(1)} s` : `${Math.round(ms / 1000)} s`;

/** Live checklist on Review briefing while the draft briefing is written. */
const BriefingProgress: React.FC<Props> = ({ file, variant = "live" }) => {
  const rows = briefingRows(file);
  const stalled =
    !!rows?.some(r => r.status === "stalled") || briefingStalled(file);
  const anyActive = !stalled && !!rows?.some(r => r.status === "active");
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!anyActive) return undefined;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [anyActive]);

  if (!rows) {
    // A briefing started before this checklist existed: the old one-line status.
    return (
      <div
        className="flex flex-col items-center gap-2 rounded-xl border border-gray-200 bg-white py-14 text-center"
        role="status"
      >
        {stalled ? (
          <Clock className="h-7 w-7 text-gray-400" aria-hidden />
        ) : (
          <Loader2
            className="h-7 w-7 animate-spin text-[#974ae4]"
            aria-hidden
          />
        )}
        <div className="text-sm font-semibold text-gray-900">
          {stalled
            ? "The draft briefing is taking longer than usual"
            : "Writing the draft briefing…"}
        </div>
        <div className="text-xs text-gray-500">
          {stalled
            ? STALLED_LINE
            : "Usually under a minute. Every item is checked against its slide."}
        </div>
      </div>
    );
  }

  const elapsed = (startedAt?: string) => {
    const t = startedAt ? Date.parse(startedAt) : NaN;
    return Number.isNaN(t) ? null : Math.max(0, now - t);
  };

  return (
    <div
      role={variant === "live" ? "status" : undefined}
      data-testid="briefing-progress"
      data-variant={variant}
      className={
        variant === "live"
          ? "rounded-xl border border-purple-200 bg-purple-50/40 px-4 py-3 text-sm text-gray-700"
          : "text-sm text-gray-700"
      }
    >
      {variant === "live" && (
        <>
          <div className="font-medium text-gray-800">
            {stalled
              ? `The draft briefing for v${file.plan_version ?? file.version} is taking longer than usual`
              : `Writing the draft briefing for v${file.plan_version ?? file.version}`}
          </div>
          <div className="text-xs text-gray-500">
            {stalled
              ? "Izba has been told. There is nothing for you to do; you'll be notified when it's ready to review."
              : "Usually under a minute. You can leave this page; it keeps going and you'll be notified when it's ready to review."}
          </div>
        </>
      )}
      <ol
        className="mt-3 space-y-2 rounded-lg border border-gray-100 bg-white/70 p-3"
        aria-label="Briefing progress"
      >
        {rows.map(r => {
          // Once a pass has stalled, nothing else is really running.
          const status: RowStatus =
            stalled && r.status === "active" ? "pending" : r.status;
          const running = status === "active" ? elapsed(r.startedAt) : null;
          // While stalled, the stuck step is the story: the others are one line each.
          const quiet = stalled && status !== "stalled";
          return (
            <li
              key={r.key}
              data-testid={`briefing-${r.key}`}
              data-status={status}
              className="flex flex-wrap items-start gap-x-2"
            >
              {status === "done" ? (
                <CheckCircle2
                  className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600"
                  aria-label="done"
                />
              ) : status === "active" ? (
                <Loader2
                  className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-[#974ae4]"
                  aria-label="in progress"
                />
              ) : status === "stalled" ? (
                <Clock
                  className="mt-0.5 h-4 w-4 shrink-0 text-gray-500"
                  aria-label="taking longer than usual"
                />
              ) : (
                <Circle
                  className="mt-0.5 h-4 w-4 shrink-0 text-gray-300"
                  aria-label="pending"
                />
              )}
              <span
                className={`flex-1 ${
                  status === "pending"
                    ? "text-gray-400"
                    : status === "active"
                      ? "font-medium text-purple-800"
                      : "text-gray-800"
                }`}
              >
                {r.label}
              </span>
              {status === "stalled" && (
                <span
                  className="text-xs text-gray-500"
                  data-testid={`briefing-${r.key}-stalled`}
                >
                  {STALLED_LINE}
                </span>
              )}
              {status === "done" && r.ms != null && !quiet && (
                <span className="font-mono text-xs text-gray-400">
                  {seconds(r.ms)}
                </span>
              )}
              {running != null && (
                <span className="font-mono text-xs text-purple-700">
                  {Math.floor(running / 1000)} s…
                </span>
              )}
              {r.items && status !== "pending" && !quiet && (
                <StepItems rowKey={`briefing-${r.key}`} items={r.items} />
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default BriefingProgress;
